import { useCallback, useMemo, useState } from 'react';
import styles from './CTPreset.module.scss';
import Button from '@components/Button';
import Input from '@components/Input';
import { ReactComponent as Close } from 'img/btn-close.svg';
import { defaultPreset, PresetType } from 'config/analysisConfig';
import { usePanelContainer } from '@features/analysis/rendering/Panels/usePanelContainer';
import { PanelComponentKeyType } from 'config/panelConfig';
import { CT_PRESET_CONTENT_NAMES } from 'config/labels';
import { configStore } from 'config/configStore';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';

export type CTPresetProps = {
  // componentKey: PanelComponentKeyType;
  onClose?: () => void;
};
type TempPresetType = {
  label: string;
  windowLevel: string;
  windowWidth: string;
};
// type KeyedPreset = {key: string, value: TempPresetType};

// 보다 맘
const CTPreset = ({ onClose }: CTPresetProps) => {
  // const { panelStates } = usePanelContainer();
  const { currentVOI, updatePreset } = useAnalysisPage();
  const [version, setVersion] = useState(0);

  const { windowLevel, windowWidth } = currentVOI;
  const presets = useMemo(
    () => configStore.get().customWindowLevelPreset.presets ?? [],
    [version],
  );

  const initPreset: TempPresetType = useMemo(
    () => ({
      label: '',
      windowLevel: String(windowLevel),
      windowWidth: String(windowWidth),
    }),
    [windowLevel, windowWidth],
  );

  // 모달내의 상태
  const [preset, setPreset] = useState<TempPresetType>(initPreset);

  // 입력 핸들러
  const handleChange = useCallback(
    (partial: Partial<TempPresetType>) =>
      setPreset((prev) => ({ ...prev, ...partial })),
    [],
  );

  const handleUpdatePreset = useCallback(
    (action: 'add' | 'modify' | 'delete') => {
      let newPresets = [...presets];

      if (action === 'delete') {
        newPresets = presets.filter((p) => p.label !== preset.label);
        setPreset(initPreset);
      } else {
        const newPreset: PresetType = {
          label: preset.label,
          windowLevel: Number(preset.windowLevel),
          windowWidth: Number(preset.windowWidth),
        };

        const existingIndex = presets.findIndex(
          (p) => p.label === preset.label,
        );

        if (action === 'add' && existingIndex === -1) {
          newPresets.push(newPreset);
        } else if (action === 'modify' && existingIndex !== -1) {
          newPresets[existingIndex] = newPreset;
        }
      }

      configStore.setData({
        customWindowLevelPreset: { presets: newPresets },
      });
      setVersion((v) => v + 1);
    },
    [preset, presets, initPreset],
  );

  // 중복된 이름이 있다?
  const isDuplicated = useMemo(
    () =>
      presets.some((p) => p.label === preset.label) ||
      Object.values(defaultPreset).some((p) => p.label === preset.label),
    [preset.label, presets],
  );
  // 가능한 녀석인가?
  const isInvalid =
    !preset.label || preset.windowLevel === '' || preset.windowWidth === '';
  const isAddable = !isInvalid && !isDuplicated;

  const targetPreset = presets.find((p) => p.label === preset.label);

  const isModifiable = useMemo(() => {
    if (isInvalid || !targetPreset) return false;

    const isChanged =
      targetPreset.windowLevel !== Number(preset.windowLevel) ||
      targetPreset.windowWidth !== Number(preset.windowWidth);

    return isChanged;
  }, [preset, targetPreset, isInvalid]);

  // 추가하려면요..
  // 라벨 중복된건 무조건 없어야하고
  // 초기 상태면 가능한지 체크하고
  // 다른거 누른 상태라면 바뀐지도 체크
  // const isAddable =
  //   !isDuplicatedLabel &&
  //   !invalidPreset &&
  //   ((preset.key==='' && !invalidPreset) ||
  //   (preset.key!=='' && isModified))

  // 바꿀라면요..
  // 라벨 중복된게 있을 수 있어. 그러나 숫자는 바껴야해.
  // 숫자가 같을 수 있어. 그러면 라벨은 바껴야지
  const handleSelect = useCallback((item: PresetType) => {
    setPreset({
      label: item.label,
      windowLevel: String(item.windowLevel),
      windowWidth: String(item.windowWidth),
    });
  }, []);

  // const InputField = ({
  //   label,
  //   children,
  //   type,
  // }: {
  //   label: React.ReactNode;
  //   children: React.ReactNode;
  //   type: 'input' | 'label';
  // }) => {
  //   return (
  //     <div className={styles.fileInput}>
  //       <div className={styles.textInput}>
  //         <div className={styles.label}>
  //           <div className={styles.labelText}>{label}</div>
  //         </div>
  //         {children}
  //       </div>
  //     </div>
  //   );
  // };

  const handleNumberChange =
    (key: 'windowWidth' | 'windowLevel') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      const numValue = Number(newValue);

      if (newValue === '' || newValue === '-') {
        handleChange({ [key]: newValue });
        return;
      }

      if (/^-?(0|[1-9]\d*)$/.test(newValue)) {
        if (Object.is(numValue, -0)) {
          handleChange({ [key]: '' });
        } else {
          handleChange({ [key]: newValue });
        }
      }
    };

  return (
    <div className={styles.controller}>
      <div className={styles.menuItemGroup}>
        <div className={styles.Editor}>
          <span>{CT_PRESET_CONTENT_NAMES.editor}</span>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close CT preset modal"
          >
            <Close />
          </button>
        </div>
        {presets.map((item) => (
          <div
            key={item.label}
            className={`${styles.Editor} ${
              item.label === preset.label ? styles.selected : ''
            }`}
            onClick={() => handleSelect(item)}
          >
            {item.label}
          </div>
        ))}
        <div className={styles.dividerContent}>
          <div className={styles.divider} />
        </div>

        <div className={styles.menuItemlabel}>
          <div className={styles.fileInput}>
            <div className={styles.textInput}>
              <div className={styles.label}>
                <div className={styles.labelText}>
                  {CT_PRESET_CONTENT_NAMES.label}
                </div>
              </div>
              <div className={styles.input}>
                <input
                  className={styles.inputText}
                  placeholder="Pick File"
                  value={preset.label}
                  onChange={(e) => handleChange({ label: e.target.value })}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={styles.windowContent}>
          <div className={styles.menuItemWindowWidth}>
            <div className={styles.fileInput}>
              <div className={styles.textInput}>
                <div className={styles.label}>
                  <div className={styles.labelText}>
                    {CT_PRESET_CONTENT_NAMES.windowWidth}
                  </div>
                </div>
                <div className={styles.input}>
                  <input
                    placeholder={CT_PRESET_CONTENT_NAMES.windowWidth}
                    value={preset.windowWidth}
                    onChange={handleNumberChange('windowWidth')}
                    className={styles.inputText}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={styles.menuItemWindowLevel}>
            <div className={styles.fileInput}>
              <div className={styles.textInput}>
                <div className={styles.label}>
                  <div className={styles.labelText}>
                    {CT_PRESET_CONTENT_NAMES.windowLevel}
                  </div>
                </div>
                <div className={styles.input}>
                  <input
                    placeholder={CT_PRESET_CONTENT_NAMES.windowLevel}
                    value={preset.windowLevel}
                    onChange={handleNumberChange('windowLevel')}
                    className={styles.inputText}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.menuItemButton}>
          <div className={styles.buttonGroup}>
            <Button
              className={styles.add}
              disabled={!isAddable}
              onClick={() => handleUpdatePreset('add')}
            >
              {CT_PRESET_CONTENT_NAMES.add}
            </Button>

            <Button
              className={styles.modify}
              disabled={!isModifiable}
              onClick={() => handleUpdatePreset('modify')}
            >
              {CT_PRESET_CONTENT_NAMES.modify}
            </Button>
          </div>
          <Button
            className={styles.delete}
            disabled={!preset.label}
            onClick={() => handleUpdatePreset('delete')}
          >
            {CT_PRESET_CONTENT_NAMES.remove}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CTPreset;
